import { useTranslation } from 'react-i18next';

import type { PrioritySlice } from '../../types';
import { Donut, type DonutSlice } from '@/shared/charts/Donut';
import { Mono } from '@/shared/ui/atoms/Mono';
import './PriorityDonut.styl';

type Props = { priorities: PrioritySlice[] };

const TONE: Record<PrioritySlice['priority'], DonutSlice['tone']> = { HIGH: 'primary', MEDIUM: 'primary-2', LOW: 'primary-4' };

/** Open tasks by priority. One hue, three lightness steps: darker means more urgent. */
export function PriorityDonut({ priorities }: Props) {
  const { t } = useTranslation();
  const ordered = (['HIGH', 'MEDIUM', 'LOW'] as const).map((p) => priorities.find((x) => x.priority === p) ?? { priority: p, count: 0, pct: 0 });
  const total = ordered.reduce((s, x) => s + x.count, 0);
  return (
    <div className="f-prio">
      <Donut
        slices={ordered.map((p) => ({ key: p.priority, label: t(`tasks.priority.${p.priority}`), value: p.count, tone: TONE[p.priority] }))}
        label={t('insights.priority.aria', { count: total })}
        center={
          <>
            <Mono className="f-prio__total">{total}</Mono>
            <span className="f-prio__open">{t('insights.priority.open')}</span>
          </>
        }
      />
      <ul className="f-prio__legend">
        {ordered.map((p) => (
          <li key={p.priority} className="f-prio__item">
            <span className={`f-prio__dot f-prio__dot--${TONE[p.priority]}`} aria-hidden="true" />
            {t(`tasks.priority.${p.priority}`)} <Mono as="b">{p.count}</Mono>
          </li>
        ))}
      </ul>
    </div>
  );
}
